import { Injectable, inject } from '@angular/core';
import { ReplaySubject, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CmsApiService } from './api/cms-api.service';
import { LanguageService } from './language.service';

interface CurrencyRate {
  from: string;
  to: string;
}

@Injectable({
  providedIn: 'root'
})
export class CurrencyService {
  private _language = inject(LanguageService);
  private _api = inject(CmsApiService);


  public currentRate = null;

  private _cashedRate = {};

  /**
   * Required to get amount from cents
   */
  private _centDividersList: any = {
    'EUR': 100, // Required default param
  };

  /**
   * Return amount & currency for exist locale
   * @param value
   * @param currency
   * @param spacer
   */
  transformLocaleCurrency(value: any, currency: string, spacer): any {
    return this._language.rightLocales.rtCurrencies.includes((currency || '').toUpperCase()) &&
      this._language.rightLocales.rtLanguages.includes(this._language.current)
      ? value + spacer + currency
      : currency + spacer + value;
  }

  /**
   * Convert cents to amount
   *
   * @param amount
   * @param currency
   */
  public centsToAmount(amount: number, currency: string): number {
    if (this._centDividersList.hasOwnProperty(currency)) {
      return amount / this._centDividersList[currency];
    } else {
      return amount / this._centDividersList['EUR']; // if in array not exists required currency - divide to EUR
    }
  }

  /**
   * Convert amount to cents
   *
   * @param amount
   * @param currency
   */
  public amountToCents(amount: number, currency: string): number {
    if (this._centDividersList.hasOwnProperty(currency)) {
      return amount * this._centDividersList[currency];
    } else {
      // if in array not exists required currency - divide to EUR
      return amount * this._centDividersList['EUR'];
    }
  }

  /**
   * Refresh rates currencies
   * @param currencies
   */
  private _refreshRate(currencies: CurrencyRate) {
    const check = JSON.stringify(currencies);
    if (this._cashedRate[check]) {
      this.currentRate = this._cashedRate[check];
      return of(this._cashedRate[check]);
    } else {
      if (currencies.from === currencies.to) {
        this._cashedRate[check] = 1;
        this.currentRate = 1;
        return of(this._cashedRate[check]);
      }
      return this._api.getExchangeRate(currencies).pipe(
        map(response => {
          if (response?.data?.rate) {
            return Number(response.data.rate);
          } else {
            return null;
          }
        }),
        tap(rate => {
          this._cashedRate[check] = rate;
          this.currentRate = rate;
        })
      ) as ReplaySubject<any>;
    }
  }

  /**
   * Calculate and get CurrencyTo
   * @param value
   */
  public getValueCurrencyTo(value: number, currencies: CurrencyRate): ReplaySubject<number> {
    return this._refreshRate(currencies).pipe(
      map(rate => Number(value) * rate)
    ) as ReplaySubject<number>;
  }

  /**
   * Calculate and get CurrencyFrom
   * @param value
   */
  public getValueCurrencyFrom(value: number, currencies: CurrencyRate): ReplaySubject<number> {
    return this._refreshRate(currencies).pipe(
      map(rate => Number(value) / rate)
    ) as ReplaySubject<number>;
  }

}
